import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetFinancialStatisticsQuery } from '@app/api/Analytics';
import { useGetEmployeesWithoutWorkScheduleQuery } from '@app/api/WorkSchedule';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useErrorHandler } from '@app/shared/hooks';

import { useCalendarContext } from '@pages/Calendar/context';

export const useCalendarHeader = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const {
    userAccessRights: {
      WorkSchedules: { IsOn, IsCreate, IsEdit, IsEditPast },
      Records: {
        IsOn: RecordsIsOn,
        IsJournal: RecordsIsJournal,
        IsFinancialStatistics,
      },
    },
  } = useNewUserAccessRights();
  const {
    todayMoment,
    selectedDateMoment,
    selectedDateTitle,
    isTodaySelected,
    goToPrevDayHandler,
    goToNextDayHandler,
    returnToTodayHandler,
  } = useCalendarContext();

  const dateString = useMemo(
    () => selectedDateMoment.format('YYYY-MM-DD'),
    [selectedDateMoment]
  );

  const {
    data: financialStatistics,
    isLoading: isFinancialStatisticsLoading,
    isFetching: isFinancialStatisticsFetching,
    error: financialStatisticsError,
    isError: isFinancialStatisticsError,
  } = useGetFinancialStatisticsQuery(
    {
      ActiveBranchId: ActiveBranchId,
      dateS: dateString,
      dateE: dateString,
    },
    { skip: ActiveBranchId == null || !IsFinancialStatistics }
  );

  const {
    data: employeesWithoutWorkSchedule = [],
    isLoading: isAddEmployeeModalLoading,
    isFetching: isAddEmployeeModalFetching,
    error: employeesWithoutWorkScheduleError,
    isError: isEmployeesWithoutWorkScheduleError,
  } = useGetEmployeesWithoutWorkScheduleQuery(
    {
      ActiveBranchId: ActiveBranchId,
      date: dateString,
    },
    { skip: ActiveBranchId == null || !isAddEmployeeModalOpen }
  );

  useErrorHandler([
    { flag: isFinancialStatisticsError, error: financialStatisticsError },
    {
      flag: isEmployeesWithoutWorkScheduleError,
      error: employeesWithoutWorkScheduleError,
    },
  ]);

  const hasUserRightToAddExecutor = useMemo(() => {
    const hasBaseAccess =
      IsOn && IsCreate && IsEdit && RecordsIsOn && RecordsIsJournal;

    if (!hasBaseAccess) {
      return false;
    }

    if (selectedDateMoment.isSame(todayMoment, 'day')) {
      return true;
    }

    if (selectedDateMoment.isAfter(todayMoment, 'day')) {
      return true;
    }

    return IsEditPast;
  }, [
    IsOn,
    IsCreate,
    IsEditPast,
    IsEdit,
    RecordsIsJournal,
    RecordsIsOn,
    selectedDateMoment,
    todayMoment,
  ]);

  useEffect(() => {
    if (employeesWithoutWorkScheduleError) {
      setIsAddEmployeeModalOpen(false);
    }
  }, [employeesWithoutWorkScheduleError]);

  const handleOpenAddEmployeeModal = useCallback(() => {
    setIsAddEmployeeModalOpen(true);
  }, []);

  const handleCloseAddEmployeeModal = useCallback(() => {
    setIsAddEmployeeModalOpen(false);
  }, []);

  return {
    selectedDateTitle,
    isTodaySelected,
    goToPrevDayHandler,
    goToNextDayHandler,
    returnToTodayHandler,
    hasUserRightToAddExecutor,
    IsFinancialStatistics,
    dateString,
    financialStatistics,
    isFinancialStatisticsLoading:
      isFinancialStatisticsLoading || isFinancialStatisticsFetching,
    isAddEmployeeModalOpen,
    isAddEmployeeModalLoading:
      isAddEmployeeModalLoading || isAddEmployeeModalFetching,
    employeesWithoutWorkSchedule,
    handleOpenAddEmployeeModal,
    handleCloseAddEmployeeModal,
  };
};
